import { useCallback, useState } from 'react';
import type { GroupedResult, SearchResult } from '@components/chat/searchResults';

interface Options {
  /** Awaited, and only on the unselected -> selected transition. */
  onAddToContext?: (results: SearchResult[]) => Promise<void>;
  /** File ids the parent already holds in context; those rows lock. */
  selectedFileIds: string[];
}

export function useSearchResultSelection({ onAddToContext, selectedFileIds }: Options) {
  // Keyed by fileId (the grouping key), not result.id: two files can share an
  // id-less title, and the group is what the checkbox acts on.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openSegments, setOpenSegments] = useState<Set<string>>(new Set());

  const isInContext = useCallback(
    (group: GroupedResult) =>
      [group.primary, ...group.additional].some(
        (result) => result.id && selectedFileIds.includes(result.id),
      ),
    [selectedFileIds],
  );

  const toggleSelected = useCallback(
    async (group: GroupedResult) => {
      const wasSelected = selected.has(group.fileId);
      setSelected((prev) => {
        const next = new Set(prev);
        if (wasSelected) next.delete(group.fileId);
        else next.add(group.fileId);
        return next;
      });

      // Deselecting is local only — the parent owns removal from context.
      if (!wasSelected && onAddToContext) await onAddToContext([group.primary]);
    },
    [onAddToContext, selected],
  );

  const toggleSegments = useCallback((fileId: string) => {
    setOpenSegments((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  }, []);

  return {
    isSelected: (fileId: string) => selected.has(fileId),
    isInContext,
    toggleSelected,
    showsSegments: (fileId: string) => openSegments.has(fileId),
    toggleSegments,
  };
}
